import PropTypes from 'prop-types';
import FormRow from '@/components/core/form/FormRow';
import { useFormContext } from 'react-hook-form';
import get from 'just-safe-get';
import ErrorMessage from './ErrorMessage';
import ConversionField from '../ConversionField';
import usePatchParams from '../usePatchParams';
import { useEffect, useRef } from 'react';
import { isDefined } from '@/utils/miscUtils';
import InputBase from '@/components/core/form/InputBase';
import { NumericFormatInput } from '@/components/core/form/NumericFormatInput';
import { useParamsContext } from '@/components/providers/useParamsContext';
import AutoEligibleField from './AutoEligibleField';
import { DefaultSourceDisplay } from '../common/DefaultSourceDisplay';
import MCFieldsSummary from '@/components/nodes/MCFieldsSummary';
import DataSourcesField from './DataSourcesField';
import { DISPLAY_TYPES } from '@/consts';
import useBetterCostDataModal from '@/components/nodes/useBetterCostDataModal';

const InputField = ({
  param,
  paramSchema,
  name,
  parentName,
  onChange,
  displayType = DISPLAY_TYPES.row,
  isGroupField = false,
}) => {
  const {
    label,
    type,
    tooltip,
    source,
    mc_eligible: isMcEligible,
    convertible_units: convertibleUnits,
    editable,
    auto_eligible: isAutoEligible,
    data_sources: dataSources,
  } = paramSchema;
  const {
    primary: {
      value,
      is_default: isDefault,
      default: defaultValue,
      default_source: defaultSource,
      value_raw: valueRaw,
    } = {},
    mc_min,
    mc_max,
    unit,
  } = param ?? {};
  const hasEcoivent = dataSources?.some(source => source === 'ecoinvent');
  const isUncertaintyActive = param?.uncertainty_active?.value;

  const {
    control,
    register,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();
  const { readOnly } = useParamsContext();
  const isAuto = value?.auto === true;
  const disabled = !editable || readOnly || isAuto;
  const isYearField = name.endsWith('_year');
  const { checkForSuggestion, betterCostDataModal } = useBetterCostDataModal({ resetParams: true });

  const error = get(errors, name);
  const errorMessage = isGroupField
    ? errors?.[parentName]?.message?.[paramSchema.name]?.primary?.value
    : error?.message?.primary?.value;
  const { patchPrimary } = usePatchParams();
  const { ref: inputRef, ...registration } = register(name, { valueAsNumber: type === 'numeric', disabled });
  const numericValueRef = useRef(null);

  const events = {
    onChange: e => {
      e.preventDefault();
    },
    onBlur: async e => {
      const prevValue = getValues(name);
      const value = type === 'numeric' ? (numericValueRef.current ?? parseFloat(e.target.value)) : e.target.value;

      if (prevValue !== value) {
        if (isGroupField && onChange) {
          setValue(name, value, { shouldValidate: true, shouldDirty: true });
          registration.onChange(e);
          onChange(e);
        } else {
          await patchPrimary(name, value);
          if (isYearField) {
            await checkForSuggestion();
          }
        }
      } else {
        setValue(name, value, { shouldValidate: true, shouldDirty: false });
      }
    },
    onCopy: e => {
      e.preventDefault();
      const currentValue = type === 'numeric' ? numericValueRef.current : getValues(name);
      e.clipboardData.setData('text/plain', valueRaw || currentValue);
    },
  };

  useEffect(() => {
    if (isDefined(value)) {
      if (isAuto) {
        setValue(name, 'Auto', { shouldValidate: false, shouldDirty: false });
      } else {
        setValue(name, value, { shouldValidate: true, shouldDirty: false });
      }
    }
  }, [value, name, setValue, isAuto]);

  const isNumeric = type === 'numeric' && !name.endsWith('_year') && !isAuto;

  const inputElement = isNumeric ? (
    <NumericFormatInput
      id={name}
      name={name}
      disabled={disabled}
      readOnly={readOnly}
      control={control}
      {...events}
      placeholder={defaultValue?.toString()}
      hasError={!!errorMessage}
      isDefault={isDefault}
      onNumericChange={value => {
        numericValueRef.current = value;
      }}
    />
  ) : (
    <InputBase
      id={name}
      name={name}
      type="text"
      disabled={disabled}
      readOnly={readOnly}
      defaultValue={value}
      placeholder={defaultValue?.toString()}
      hasError={!!errorMessage}
      isDefault={isDefault}
      ref={inputRef}
      {...events}
    />
  );

  if (displayType === DISPLAY_TYPES.table) {
    return (
      <>
        <tr>
          <td className="text-sm p-2 pl-4">{label}</td>
          <td className="p-2">{inputElement}</td>
          <td className="p-2 pr-4 w-full">
            {convertibleUnits?.length > 0 && (
              <ConversionField
                className="w-full"
                unit={unit}
                name={`${parentName}.conversion.${name}`}
                paramName={name}
                units={convertibleUnits}
                disabled={disabled}
                readOnly={readOnly}
              />
            )}
          </td>
        </tr>
        {errorMessage && (
          <tr>
            <td></td>
            <td className="p-0 pl-1">
              <ErrorMessage text={errorMessage} className="p-0" />
            </td>
            <td></td>
          </tr>
        )}
      </>
    );
  }

  if (displayType === DISPLAY_TYPES.inline) {
    const conversionName = parentName ? `${parentName}.conversion.${name}` : `conversion.${name}`;
    return (
      <div className={`${convertibleUnits?.length > 0 ? 'join flex' : ''}`}>
        {inputElement}
        {convertibleUnits?.length > 0 && (
          <ConversionField
            unit={unit}
            name={conversionName}
            paramName={name}
            units={convertibleUnits}
            disabled={disabled}
            readOnly={readOnly}
          />
        )}
        {errorMessage && <ErrorMessage text={errorMessage} className="p-0 text-xs" />}
      </div>
    );
  }

  return (
    <>
      <FormRow
        label={label}
        tooltip={tooltip}
        source={source}
        name={name}
        dataSources={hasEcoivent && <DataSourcesField name={name} paramSchema={paramSchema} param={param} />}
        auto={isAutoEligible && <AutoEligibleField name={name} value={value?.auto} readOnly={readOnly} />}
      >
        <div className="flex flex-col flex-1">
          <div className={`${convertibleUnits?.length > 0 ? 'join flex' : ''}`}>
            {inputElement}
            {convertibleUnits?.length > 0 && (
              <ConversionField
                unit={unit}
                name={`conversion.${name}`}
                paramName={name}
                units={convertibleUnits}
                disabled={disabled}
                readOnly={readOnly}
              />
            )}
          </div>
          <ErrorMessage text={errorMessage} />
        </div>
      </FormRow>
      <div className="flex flex-row gap-2 flex-1">
        <div className="flex-1">
          {isMcEligible && !isAuto && isUncertaintyActive && (
            <MCFieldsSummary min={mc_min?.value} max={mc_max?.value} />
          )}
        </div>
        <DefaultSourceDisplay source={defaultSource} />
      </div>
      {isYearField && betterCostDataModal}
    </>
  );
};

InputField.propTypes = {
  param: PropTypes.object,
  paramSchema: PropTypes.object,
  name: PropTypes.string,
  parentName: PropTypes.string,
  onChange: PropTypes.func,
  displayType: PropTypes.oneOf(['row', 'table', 'inline']),
  isGroupField: PropTypes.bool,
  children: PropTypes.node,
};

export default InputField;
